import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { NToggle, ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BehaviorSubject } from 'rxjs';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { OverlayComponent } from '../../common/overlay/overlay.component';
import { iconsOptions } from '../../styles/cms.config';
import { PageSearchOutletComponent } from '../search/outlet/page-search-outlet.component';
import { NPageSearch } from '../search/page-search.interfaces';
import { PageOffCanvasMenuComponent } from './menu/page-off-canvas-menu.component';
import { PageOffCanvasSearchFormComponent } from './search-form/page-off-canvas-search-form.component';

export namespace NPageOffCanvas {
    export const BLOCK_ID = 'off-canvas-menu';

    export const MENU_COMPONENT_ID = 'page-off-canvas-menu';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IMenu extends IMenuItem {
        /**
         * Off canvas menu
         */
        menu: IMenuItem[];

        /**
         * Featured items
         */
        promoTiles?: IPromoTile[];

        /**
         * Reopen policy
         */
        resetOnClose?: boolean;
    }

    /**
     * Interface for menu component
     * We fake component data here to use the component nested
     */
    export interface IMenuComponentData extends IMenu, NICmsContentComponent.IData {}

    /**
     * Block data
     */
    export interface IBlockDataContent extends IMenu {
        bottomLinks: IBottomLink[];
    }

    /**
     * Main menu item
     */
    export interface IMenuItem {
        /**
         * Item type
         * either link or menu opener
         */
        type: EMenuItemType;
        /**
         * Item label
         */
        text: string;
        /**
         * Sub-menu (EMenuItemType.menu only)
         */
        subItem: IMenu;
        buttonCopy?: string;
        link: string;
        listOfLinks?: { text: string; url: string }[];
    }

    /**
     * Bottom menu item
     */
    export interface IBottomLink {
        icon?: string;
        text: string;
        description?: string;
        link: string;
    }

    export interface IPromoTile {
        image: string;
        title: string;
        description: string;
        buttonCopy?: string;
        link: string;
    }

    export enum EMenuItemType {
        link,
        menu,
        listOfLinks
    }

    const fields: NICmsContentField.IField[] = [
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            required: true,
            options: [
                { key: NPageOffCanvas.EMenuItemType.menu, title: 'Submenu' },
                { key: NPageOffCanvas.EMenuItemType.link, title: 'Link' },
                { key: NPageOffCanvas.EMenuItemType.listOfLinks, title: 'List of links' }
            ],
            default: NPageOffCanvas.EMenuItemType.link
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.text,
            title: 'Text',
            default: 'Item',
            required: true
        },
        {
            key: 'buttonLabel',
            title: 'Button',
            type: NICmsContentField.EFieldType.label,
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type === NPageOffCanvas.EMenuItemType.listOfLinks
        },
        {
            key: 'buttonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Text',
            description: 'The button will not appear if not set',
            default: 'View all',
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type === NPageOffCanvas.EMenuItemType.listOfLinks
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            default: '/',
            required: true,
            canShow: (data: NPageOffCanvas.IMenuItem) =>
                data?.type === NPageOffCanvas.EMenuItemType.link || data?.type === NPageOffCanvas.EMenuItemType.listOfLinks
        },
        <NICmsContentField.IRepeaterField>{
            key: 'listOfLinks',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Links',
            default: [],
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type === NPageOffCanvas.EMenuItemType.listOfLinks,
            required: true,
            itemName: 'Link',
            itemNameField: 'text',
            fields: [
                <NICmsContentField.IField>{
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Text',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'url',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        }
    ];
    const menuField = <NICmsContentField.IRepeaterField<IMenuItem>>{
        key: 'menu',
        type: NICmsContentField.EFieldType.repeater,
        title: 'Menu',
        itemName: 'Menu item',
        itemNameField: 'text',
        required: true,
        complex: true,
        default: [],
        // note that type will not defined for the top level component
        canShow: (data: NPageOffCanvas.IMenuItem) =>
            data?.type !== NPageOffCanvas.EMenuItemType.link && data?.type !== NPageOffCanvas.EMenuItemType.listOfLinks,
        fields
    };
    fields.push(menuField);
    export const MENU_FIELDS = [
        menuField,
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'bottomLinks',
            title: 'Bottom Links',
            itemName: 'Item',
            itemNameField: 'text',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    key: 'icon',
                    previewMode: 'icon',
                    options: iconsOptions
                },
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true
                },
                {
                    key: 'description',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Description'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'promoTiles',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Promo Tiles',
            itemName: 'Tile',
            itemNameField: 'title',
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true
                },
                {
                    key: 'description',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Description',
                    required: true
                },
                {
                    key: 'buttonCopy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button copy',
                    description: 'The button will not appear if not set',
                    default: 'Shop now'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true,
                    description: 'Link on the entire item including image and copies'
                }
            ]
        },
        {
            key: 'resetOnClose',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Reset on close',
            default: false
        }
    ];

    export enum EOpenState {
        closed = 0,
        openMenu = 1,
        openSearch = 2
    }

    export const OFF_CANVAS_GROUP_ID = 'offCanvas';
}

@Component({
    imports: [
        ToggleModule,
        OverlayComponent,
        PageSearchOutletComponent,
        PageOffCanvasSearchFormComponent,
        DfUiCarouselModule,
        NgClass,
        HrefComponent,
        PageOffCanvasMenuComponent,
        ButtonStyleComponent,
        ICmsClientModule,
        DfImageDirective
    ],
    selector: 'page-off-canvas',
    templateUrl: 'page-off-canvas.html',
    styleUrl: 'page-off-canvas.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageOffCanvasComponent>({
    id: NPageOffCanvas.BLOCK_ID,
    block: {
        url: NPageOffCanvas.BLOCK_ID,
        scope: 'website',
        autoload: 'prerender'
    },
    onDemandOnly: true,
    name: 'Mobile menu',
    description: 'Mobile menu block',
    fields: [...NPageOffCanvas.MENU_FIELDS]
})
export class PageOffCanvasComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @Input()
    offCanvasState!: BehaviorSubject<NPageOffCanvas.EOpenState>;

    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageOffCanvas.IBlockDataContent;

    /**
     * I failed with subcomponents
     */
    protected override canDetach = false;

    /**
     * Shift level
     */
    level = new BehaviorSubject(0);

    @ViewChild('stage', { read: ElementRef })
    stage?: ElementRef;

    @ViewChild('cmsNode', { read: ElementRef, static: false })
    node?: ElementRef<HTMLElement>;

    readonly toggleGroupId = NPageOffCanvas.OFF_CANVAS_GROUP_ID;
    readonly searchMeganavKey = NPageSearch.SEARCH_MEGANAV_KEY;

    protected readonly EOffCanvasClosedState = NPageOffCanvas.EOpenState.closed;
    protected readonly overlayVisible$ = new BehaviorSubject<boolean>(false);

    private readonly app = inject(App);
    private readonly toggleService = inject(ToggleService);
    private readonly renderer2 = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);

    get searchToggleId(): NToggle.TId {
        return PageOffCanvasSearchFormComponent.toggleId;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.app.meta.routerEvents.subscribe(() => this.close()));
        this.subscriptions.add(
            this.offCanvasState.subscribe(state => this.overlayVisible$.next(state !== NPageOffCanvas.EOpenState.closed))
        );
    }

    /**
     * Close menu
     */
    close(deep = false) {
        const searchToggle = this.toggleService.getItem(this.searchToggleId);

        if (!deep && searchToggle.isOpen && this.offCanvasState.value === NPageOffCanvas.EOpenState.openMenu) {
            searchToggle.close();
        } else {
            if (this.offCanvasState.value) {
                this.offCanvasState.next(NPageOffCanvas.EOpenState.closed);

                if (this.content?.resetOnClose) {
                    this.setLevel(0);
                }
            }
        }
    }

    /**
     * Updates level shift
     */
    setLevel(level: number) {
        this.level.next(level);
        this.renderer2.setStyle(this.stage?.nativeElement, 'transform', `translateX(-${100 * level}%)`);
        level === 0
            ? this.renderer2.removeClass(this.elementRef.nativeElement, '_isSubmenu')
            : this.renderer2.addClass(this.elementRef.nativeElement, '_isSubmenu');
    }

    back() {
        this.setLevel(this.level.getValue() - 1);
    }
}
